import { useState } from "react";
import { KeyRound, Plus } from "lucide-react";
import {
  Page,
  Card,
  ChangePasswordModal,
  Table,
  type Column,
  Button,
  Badge,
  Text,
  DateTimeField,
  EmptyState,
} from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import { useAuth, useIsAdmin } from "../../auth/useAuth";
import type { User } from "../../auth/queries";
import { useUserList } from "../../lib/queries/users";
import { CreateUserModal, EditUserModal, RoleBadge } from "./UserModals";

/**
 * The lib's ChangePasswordModal drives the same `useChangePassword` mutation
 * this card used to hand-roll; only the length floor is the app's (backend
 * policy is 10, the lib default is 8).
 */
function ChangePasswordCard() {
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <Card.Header
        title="Change password"
        subtitle="all other sessions are signed out afterwards"
      />
      <Card.Content>
        <Button variant="primary" onClick={() => setOpen(true)}>
          <KeyRound size={16} aria-hidden="true" />
          Change password
        </Button>
      </Card.Content>
      <ChangePasswordModal
        open={open}
        minLength={10}
        onClose={() => setOpen(false)}
        onSuccess={() => {
          setOpen(false);
          notifySuccess("The password was changed. Other sessions were signed out.");
        }}
      />
    </Card>
  );
}

type UserModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; user: User };

function UsersCard() {
  const { user: me } = useAuth();
  const users = useUserList();
  const [modal, setModal] = useState<UserModalState>({ kind: "closed" });

  const columns: Column<User>[] = [
    {
      key: "username",
      header: "Username",
      primary: true,
      render: (row) => row.username,
    },
    {
      key: "role",
      header: "Role",
      render: (row) => <RoleBadge role={row.role} />,
    },
    {
      key: "active",
      header: "Status",
      render: (row) =>
        row.active ? (
          <Badge weight="ghost" variant="ok" dot>Active</Badge>
        ) : (
          <Badge weight="ghost" variant="muted" dot>Inactive</Badge>
        ),
    },
    {
      key: "created_at",
      header: "Created",
      render: (row) => (
        <Text variant="mono">
          <DateTimeField record={row} source="created_at" />
        </Text>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Table.Actions>
          <Table.Action onClick={() => setModal({ kind: "edit", user: row })}>
            Edit
          </Table.Action>
        </Table.Actions>
      ),
    },
  ];

  return (
    <>
      <Card>
        <Card.Header
          title="Users"
          subtitle="access to this admin"
          actions={
            <Button
              size="sm"
              variant="primary"
              onClick={() => setModal({ kind: "create" })}
            >
              <Plus size={14} aria-hidden="true" />
              Add
            </Button>
          }
        />
        <Card.Content padding="none">
          <Table<User>
            cards
            columns={columns}
            rows={users.data ?? []}
            rowKey={(r) => r.id}
            isLoading={users.isLoading}
            error={users.error}
            onRetry={users.refetch}
            empty={<EmptyState title="No users" />}
          />
        </Card.Content>
      </Card>

      {modal.kind === "create" && (
        <CreateUserModal onClose={() => setModal({ kind: "closed" })} />
      )}
      {modal.kind === "edit" && (
        <EditUserModal
          user={modal.user}
          isSelf={modal.user.id === me?.id}
          onClose={() => setModal({ kind: "closed" })}
        />
      )}
    </>
  );
}

export function SettingsPage() {
  const isAdmin = useIsAdmin();

  return (
    <Page>
      <Page.Header
        title="Settings"
        subtitle="account and access management"
        subtitleVariant="tagline"
      />
      <Page.Content stack gap="lg">
        <ChangePasswordCard />

        {isAdmin && <UsersCard />}
      </Page.Content>
    </Page>
  );
}
